import styled from 'styled-components';
import { Table } from 'antd';
import { Box, Flex, Text } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';

export const PageContainer = styled(Box)`
	padding: 24px;
`;

export const PageHeader = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 24px;
`;

export const HeaderLeft = styled(Flex)`
	align-items: flex-start;
	gap: 14px;
`;

export const HeaderActions = styled(Flex)`
	align-items: center;
	gap: 24px;
	flex-wrap: wrap;
	justify-content: flex-end;
`;

export const PageTitle = styled.h1`
	font-size: 24px;
	font-weight: 600;
	line-height: 1.25;
	margin: 0 0 4px 0;
	color: #18405d;
`;

export const PageSubtitle = styled.p`
	font-size: 14px;
	color: #666;
	margin: 0;
`;

export const BackButton = styled.span`
	font-size: 20px;
	cursor: pointer;
	color: #18405d;
	line-height: 1;
	/* Optically centres the arrow on the title's first line, not the whole block */
	margin-top: 6px;
	flex-shrink: 0;
`;

export const SectionCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 20px;
	margin-top: 24px;
`;

export const SectionHeader = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 16px;
`;

export const SectionTitle = styled.h2`
	font-size: 16px;
	font-weight: 600;
	margin: 0 0 4px 0;
	color: #18405d;
`;

export const SectionSubtitle = styled(Text)`
	font-size: 13px;
	color: #8c8c8c;
`;

export const FilterRow = styled(Flex)`
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`;

export const EmptyState = styled(Box)`
	text-align: center;
	padding: 48px 24px;
	color: #8c8c8c;

	.empty-title {
		font-size: 15px;
		font-weight: 600;
		color: #595959;
		margin-bottom: 6px;
	}
	.empty-body {
		font-size: 13px;
		margin-bottom: 16px;
	}
`;

export const ErrorState = styled(Box)`
	text-align: center;
	padding: 40px 24px;
	color: #b11500;
	font-size: 13px;
`;

/** Right-aligned, tabular numerics for money and count columns. */
export const NumCell = styled.span`
	display: block;
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

export const MarginCell = styled(NumCell)`
	font-weight: 600;
	color: ${(props) => (props.negative ? '#B11500' : '#267003')};
`;

export const MutedNote = styled.div`
	font-size: 12px;
	color: #8c8c8c;
`;

/* ── Project detail header ─────────────────────────────────────────────── */

export const DetailHeader = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	/* The row itself never wraps: a long project name wraps onto a second line
	   inside the left block instead, so the MTD figures and the freeze / month /
	   export controls always stay up on this top line. */
	flex-wrap: nowrap;
	margin-bottom: 20px;

	${HeaderLeft} {
		flex: 1 1 auto;
		/* min-width:0 is what actually lets a flex item narrow past its content. It is
		   needed on EVERY level down to the text: the intermediate Box wrapping the
		   title and subtitle otherwise refuses to shrink, and the name overflows on
		   top of the MTD figures instead of wrapping. */
		min-width: 0;

		> * {
			min-width: 0;
		}
	}

	${PageSubtitle} {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	${HeaderActions} {
		flex: 0 0 auto;
		flex-wrap: nowrap;
		gap: 14px;
	}

	/* Too narrow to hold both: let the actions drop to their own row instead of
	   overflowing the page, and give the title its width back. */
	@media (max-width: 1280px) {
		flex-wrap: wrap;

		${HeaderActions} {
			flex: 1 1 100%;
			flex-wrap: wrap;
		}
	}
`;

export const TitleRow = styled(Flex)`
	align-items: center;
	gap: 8px;
	flex-wrap: nowrap;
	min-width: 0;
	margin-bottom: 4px;

	/* The row owns the spacing below the title, so the heading itself sits flush */
	h1 {
		margin: 0;
		min-width: 0;
		/* A long name runs onto a second line rather than into the MTD figures, and
		   clamps there so a pathological name cannot grow the header without bound.
		   The full text is always on the title attribute. */
		font-size: 20px;
		white-space: normal;
		overflow-wrap: anywhere;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	/* The billing-model chip stays a compact tag beside the name, never shrinking
	   and never taking width from it. */
	> *:not(h1) {
		flex-shrink: 0;
		font-size: 10px;
		padding: 0 6px;
		line-height: 16px;
	}
`;

/**
 * Compact right-aligned MTD figures in the header.
 *
 * Each figure keeps its own column with a divider, and values never wrap — a lakh-scale
 * amount widens its column instead of colliding with the next one.
 */
export const HeaderStats = styled(Flex)`
	align-items: stretch;
	flex-wrap: wrap;
	justify-content: flex-end;
	row-gap: 12px;
`;

export const HeaderStat = styled(Box)`
	text-align: right;
	padding: 0 14px;
	border-left: 1px solid #ececec;

	&:first-child {
		border-left: none;
		padding-left: 0;
	}

	.stat-label {
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.5px;
		text-transform: uppercase;
		color: #8c8c8c;
		margin-bottom: 3px;
		white-space: nowrap;
	}
	.stat-value {
		font-size: 18px;
		line-height: 1.25;
		font-weight: 600;
		font-variant-numeric: tabular-nums;
		white-space: nowrap;
		color: ${(props) => {
			if (props.tone === 'danger') return '#B11500';
			if (props.tone === 'success') return '#267003';
			return '#18405d';
		}};
	}
`;

/** Pill-style tab switcher, matching the design's segmented control. */
export const TabSwitch = styled(Flex)`
	display: inline-flex;
	gap: 4px;
	padding: 4px;
	background: #f1f3f5;
	border-radius: 8px;
	margin-bottom: 20px;
`;

export const TabButton = styled.button`
	border: none;
	border-radius: 6px;
	padding: 7px 18px;
	font-size: 14px;
	font-weight: ${(props) => (props.active ? 600 : 500)};
	cursor: pointer;
	background: ${(props) => (props.active ? '#fff' : 'transparent')};
	color: ${(props) => (props.active ? '#18405d' : '#595959')};
	box-shadow: ${(props) => (props.active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none')};

	&:hover {
		color: #18405d;
	}
`;

/* ── Panel cards used across the tabs ──────────────────────────────────── */

export const Panel = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 20px;
	height: 100%;
`;

export const PanelHeader = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
	margin-bottom: 16px;
`;

export const PanelTitle = styled.h3`
	font-size: 15px;
	font-weight: 600;
	color: #18405d;
	margin: 0 0 4px 0;
`;

export const PanelSubtitle = styled.div`
	font-size: 12px;
	color: #8c8c8c;
	line-height: 1.5;
`;

/**
 * Inline "quick add" row inside a Panel.
 *
 * A grid rather than a wrapping flex row, so fields line up in even columns on every
 * breakpoint instead of leaving a ragged second line.
 */
export const QuickAddRow = styled(Box)`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
	gap: 12px 14px;
	align-items: end;
	margin-top: 4px;
`;

export const QuickAddField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 5px;
	min-width: 0;

	.field-label {
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		color: #8c8c8c;
		/* Long titles ("Cost to Customer per head (₹)") wrap onto a second line instead of
		   running into the neighbouring field */
		line-height: 1.3;
		overflow-wrap: anywhere;
	}

	/* Keep the trailing action button flush with the inputs it sits beside */
	.ant-btn {
		width: 100%;
	}
`;

/**
 * "Add a cost head" row, used by both the Fixed and Monthly panels.
 *
 * Tracks are capped so the fields never stretch across a full-width panel, and auto-fit
 * lets them wrap instead of overflowing inside a half-width one. The helper note is NOT a
 * grid column — as a column it collapses in a narrow panel and collides with the button.
 */
export const CostHeadAddRow = styled(Box)`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(150px, 215px));
	justify-content: start;
	gap: 12px 14px;
	align-items: end;
`;

/** Helper line that sits under CostHeadAddRow, full width. */
export const AddRowNote = styled.div`
	margin-top: 10px;
	font-size: 12px;
	color: #8c8c8c;
	line-height: 1.5;
`;

/** Per-date audit trail block shown under the expanded day's line items. */
export const AuditTrail = styled(Box)`
	margin-top: 16px;
	border: 1px solid #e8e8e8;
	border-radius: 6px;
	background: #fafafa;
	padding: 12px 14px;

	.audit-title {
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.5px;
		text-transform: uppercase;
		color: #8c8c8c;
		margin-bottom: 8px;
	}
	.audit-summary {
		font-size: 12px;
		color: #595959;
		margin-bottom: 6px;
	}
	.audit-line {
		font-size: 12px;
		color: #8c8c8c;
		line-height: 1.7;
		font-variant-numeric: tabular-nums;
	}
`;

/**
 * Heading above a group of line items in an expanded day, e.g. "Manpower — 14/08/2026".
 *
 * The date lives here rather than in a column: every row in the group shares it, so a
 * per-row Date column repeats the same value down the whole table.
 */
export const LineItemsTitle = styled.div`
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.4px;
	text-transform: uppercase;
	color: #8c8c8c;
	margin-bottom: 8px;
`;

/**
 * One-line stand-in for a group that carries a single number (shipments), where a full
 * table would be five columns of chrome around one value.
 */
export const LineItemsSummaryLine = styled.div`
	display: flex;
	align-items: baseline;
	gap: 8px;
	flex-wrap: wrap;
	font-size: 13px;
	color: #1f2933;
	padding: 10px 12px;
	background: #fafafa;
	border: 1px solid #ececec;
	border-radius: 6px;

	.summary-value {
		font-weight: 600;
		font-variant-numeric: tabular-nums;
	}
`;

/** Row of mini metric panels (Shipment Count, Bag Count) shown per expanded date. */
export const MiniPanelRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
`;

/**
 * Single metric card inside MiniPanelRow — a value plus its freeze state, and
 * optionally a computed money line underneath (e.g. the Cross Dock charge for bags).
 */
export const MiniPanel = styled.div`
	flex: 1 1 220px;
	min-width: 220px;
	border: 1px solid ${(props) => (props.frozen ? '#a8cfe0' : '#ececec')};
	background: ${(props) => (props.frozen ? '#F6FDFF' : '#fafafa')};
	border-radius: 6px;
	padding: 12px 14px;

	.mini-panel-label {
		font-size: 10px;
		font-weight: 600;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		color: #8c8c8c;
		margin-bottom: 6px;
	}
	.mini-panel-value {
		font-size: 18px;
		font-weight: 600;
		color: #1f2933;
		font-variant-numeric: tabular-nums;
	}
	.mini-panel-lock {
		display: flex;
		align-items: center;
		gap: 5px;
		margin-top: 6px;
		font-size: 11px;
		color: ${(props) => (props.frozen ? '#18405d' : '#8c8c8c')};
	}
	.mini-panel-sub {
		margin-top: 8px;
		font-size: 12px;
		color: #595959;
		font-variant-numeric: tabular-nums;
	}
	/* Opens the slab walk behind the count above it. A link rather than making the
	   figure itself clickable: the figure already belongs to the inline count editor. */
	.mini-panel-link {
		margin-top: 8px;
		border: none;
		background: transparent;
		padding: 0;
		font: inherit;
		font-size: 12px;
		color: #18405d;
		text-decoration: underline;
		cursor: pointer;
	}
	.mini-panel-link:hover {
		color: #0f2a3d;
	}
`;

/** Chevron expand control for the Daily Entries rows. */
export const ExpandToggle = styled.button`
	border: none;
	background: transparent;
	padding: 0 6px 0 0;
	color: #8c8c8c;
	font-size: 12px;
	line-height: 1;
	cursor: pointer;

	&:hover {
		color: #18405d;
	}
`;

/**
 * Nested table for an expanded day's line items.
 *
 * Plain antd Table rather than the shared AntdTable — that one paints a dark navy header,
 * which reads as a second top-level table instead of a detail panel.
 *
 * `&&` doubles the class to outrank AntdTable's own header rules: this table renders
 * *inside* an AntdTable expanded row, so those descendant selectors otherwise cascade in.
 */
export const NestedTable = styled(Table)`
	&& {
		.ant-table {
			background: transparent;
		}

		/*
		 * One padding rule for header, body and summary.
		 *
		 * antd sizes body cells through a .ant-table-small .ant-table-tbody > tr > td
		 * rule, which outranks a plain td.ant-table-cell — but it has no equivalent for
		 * the summary's tfoot, so the two rows ended up on different paddings and the
		 * Subtotal label sat a few pixels right of the values above it. Matching the
		 * selector depth here puts all three on the same left edge.
		 */
		.ant-table-thead > tr > th.ant-table-cell,
		.ant-table-tbody > tr > td.ant-table-cell,
		.ant-table-summary > tr > td.ant-table-cell {
			padding: 9px 12px;
		}

		th.ant-table-cell {
			background-color: #fafafa;
			color: #8c8c8c;
			font-size: 10px;
			font-weight: 600;
			letter-spacing: 0.4px;
			text-transform: uppercase;
			border-bottom: 1px solid #ececec;

			&::before {
				display: none;
			}
		}

		td.ant-table-cell {
			font-size: 13px;
			color: #1f2933;
			border-bottom: 1px solid #f5f5f5;
		}

		.ant-table-tbody > tr:last-child > td {
			border-bottom: none;
		}

		.ant-table-summary td.ant-table-cell {
			background-color: #fafafa;
			border-top: 1px solid #ececec;
			color: #1f2933;
		}
	}
`;
export const NoHoverTable = styled(AntdTable)`
	&& {
		/* 1. row hover */
		.ant-table-tbody > tr.ant-table-row:hover > td,
		.ant-table-tbody > tr > td.ant-table-cell-row-hover {
			background: transparent;
		}

		/* 2. sortable headers keep the header colour when hovered or active */
		.ant-table-thead th.ant-table-column-has-sorters:hover,
		.ant-table-thead th.ant-table-column-sort,
		.ant-table-thead th.ant-table-column-has-sorters:active,
		.ant-table-thead th.ant-table-column-has-sorters:focus {
			background-color: ${(props) => props.theme.colors.TableColor};
		}

		/* sort arrows stay legible against the navy */
		.ant-table-column-sorter,
		.ant-table-column-sorter-up,
		.ant-table-column-sorter-down {
			color: rgba(255, 255, 255, 0.55);
		}
		.ant-table-column-sorter-up.active,
		.ant-table-column-sorter-down.active {
			color: #fff;
		}

		/* 3. the sorted column must not tint its body cells */
		.ant-table-tbody > tr > td.ant-table-column-sort {
			background: transparent;
		}
	}
`;

/**
 * One adjustable count inside a MiniPanel: the figure (or the input, once Adjust is
 * pressed) on the left, its actions pinned to the panel's right edge, and the metadata
 * lines underneath — when it was uploaded, and what it bills at.
 */
export const CountEditorRow = styled.div`
	& + & {
		margin-top: 12px;
		padding-top: 12px;
		border-top: 1px dashed #e3e3e3;
	}

	.count-editor-controls {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
	}
	.count-editor-controls .ant-input-number {
		width: 120px;
	}
	.count-editor-value {
		font-size: 20px;
		font-weight: 600;
		color: #1f2933;
		font-variant-numeric: tabular-nums;
		line-height: 1.2;
	}
	.count-editor-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}
	.count-editor-frozen {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		font-size: 11px;
		color: #18405d;
	}
	.count-editor-meta {
		margin-top: 6px;
		font-size: 12px;
		color: #8c8c8c;
		font-variant-numeric: tabular-nums;
	}
`;

/**
 * The shared AntdTable with even column gutters.
 *
 * AntdTable styles only `th` (padding: 15px) and leaves body cells on antd's own
 * small-size padding, so header labels looked well spaced while the values beneath them
 * sat tight — a right-aligned money column ended up touching the timestamp next to it.
 * One rule at matching selector depth for header, body and summary puts all three on
 * the same generous horizontal padding. Mirrors NestedTable's approach: antd's
 * `.ant-table-small .ant-table-tbody > tr > td` outranks a plain `td.ant-table-cell`,
 * and there is no equivalent rule for the summary's tfoot.
 */
export const WideGutterTable = styled(AntdTable)`
	&& {
		.ant-table-thead > tr > th.ant-table-cell,
		.ant-table-tbody > tr > td.ant-table-cell,
		.ant-table-summary > tr > td.ant-table-cell {
			padding-left: 20px;
			padding-right: 20px;
		}
	}
`;

/**
 * Right-hand figure strip in a Panel header: the running arithmetic as plain text,
 * then each result as a pill. The numbers belong on the title's line rather than being
 * spelled out as a sentence in the subtitle, which leaves the subtitle free to say what
 * the panel actually is.
 */
export const PanelHeaderMetrics = styled(Flex)`
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	flex-wrap: wrap;
	/* PanelHeader top-aligns its children for the single-line panels; here the strip
	   should sit centred against the two-line title block instead. */
	align-self: center;
	flex-shrink: 0;

	.metric-formula {
		font-size: 13px;
		color: #8c8c8c;
		font-variant-numeric: tabular-nums;
		white-space: nowrap;
	}
`;

/** One figure in a PanelHeaderMetrics strip. `tone` picks fill vs outline. */
export const MetricPill = styled.span`
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
	padding: 4px 12px;
	border-radius: 14px;
	font-size: 13px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	border: 1px solid
		${(props) => {
			if (props.tone === 'success') return '#d6ecdd';
			if (props.tone === 'danger') return '#f5c6c2';
			return '#e0e0e0';
		}};
	background: ${(props) => {
		if (props.tone === 'success') return '#e9f7ee';
		if (props.tone === 'danger') return '#fbe9e7';
		return '#fff';
	}};
	color: ${(props) => {
		if (props.tone === 'success') return '#267003';
		if (props.tone === 'danger') return '#B11500';
		return '#1f2933';
	}};
`;
